import {ReactNode, useCallback, useEffect, useMemo, useRef, useState} from 'react';
import * as React from 'react';
import {createPortal} from 'react-dom';
import {noop} from '@priority-software/priority-style-react';
import {ReactComponent as IconCaret} from './chevron-down.svg';
import {IconWithPopover} from '../../../../../../../features/EmailEditor/components/IconWithPopover';
import {eventPrevented} from '../../../../../../../utils/eventPrevented';
import {createDataTestId} from '../../../../../../../utils/dataTestId';

type DropDownContextType = {
    registerItem: (ref: React.RefObject<HTMLButtonElement>) => void;
};

const DropDownContext = React.createContext<DropDownContextType | null>(null);

type EditorDropdownItemProps = {
    children: React.ReactNode;
    className: string;
    onClick: (event: React.MouseEvent<HTMLButtonElement>) => void;
    title?: string;
};

export function DropDownItem({children, className, onClick, title}: EditorDropdownItemProps) {
    const dropDownContext = React.useContext(DropDownContext);
    const ref = useRef<HTMLButtonElement>(null);

    if (dropDownContext === null) {
        throw new Error('DropDownItem must be used within a DropDown');
    }

    const {registerItem} = dropDownContext;

    useEffect(() => {
        if (ref && ref.current) {
            registerItem(ref);
        }
    }, [ref, registerItem]);

    return (
        <button className={className} onClick={onClick} ref={ref} title={title} type="button">
            {children}
        </button>
    );
}

function DropDownItems({
    children,
    dropDownRef,
    onClose
}: {
    children: React.ReactNode;
    dropDownRef: React.Ref<HTMLDivElement>;
    onClose: () => void;
}) {
    const [items, setItems] = useState<React.RefObject<HTMLButtonElement>[]>();
    const [highlightedItem, setHighlightedItem] = useState<React.RefObject<HTMLButtonElement>>();

    const registerItem = useCallback(
        (itemRef: React.RefObject<HTMLButtonElement>) => {
            setItems(prev => (prev ? [...prev, itemRef] : [itemRef]));
        },
        [setItems]
    );

    const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (!items) return;

        const key = event.key;

        if (['Escape', 'ArrowUp', 'ArrowDown', 'Tab'].includes(key)) {
            event.preventDefault();
        }

        if (key === 'Escape' || key === 'Tab') {
            eventPrevented(event, true);
            onClose();
        } else if (key === 'ArrowUp') {
            setHighlightedItem(prev => {
                if (!prev) return items[0];
                const index = items.indexOf(prev) - 1;
                return items[index === -1 ? items.length - 1 : index];
            });
        } else if (key === 'ArrowDown') {
            setHighlightedItem(prev => {
                if (!prev) return items[0];
                return items[items.indexOf(prev) + 1];
            });
        }
    };

    const contextValue = useMemo(() => ({registerItem}), [registerItem]);

    useEffect(() => {
        if (items && !highlightedItem) {
            setHighlightedItem(items[0]);
        }

        if (highlightedItem && highlightedItem.current) {
            highlightedItem.current.focus();
        }
    }, [items, highlightedItem]);

    return (
        <DropDownContext.Provider value={contextValue}>
            <div className="dropdown" ref={dropDownRef} onKeyDown={handleKeyDown}>
                {children}
            </div>
        </DropDownContext.Provider>
    );
}

export type EditorDropdownProps = {
    disabled?: boolean;
    buttonAriaLabel?: string;
    buttonClassName: string;
    buttonIconClassName?: string;
    buttonLabel?: string;
    children: ReactNode;
    stopCloseOnClickSelf?: boolean;
    portalSelector?: string;
    withPopover?: boolean;
    content?: string;
};

export default function DropDown({
    disabled = false,
    buttonLabel,
    buttonAriaLabel,
    buttonClassName,
    buttonIconClassName,
    children,
    stopCloseOnClickSelf,
    portalSelector,
    withPopover,
    content
}: EditorDropdownProps) {
    const dropDownRef = useRef<HTMLDivElement>(null);
    const buttonRef = useRef<HTMLButtonElement>(null);
    const [showDropDown, setShowDropDown] = useState(false);

    const handleClose = () => {
        setShowDropDown(false);
        if (buttonRef && buttonRef.current) {
            buttonRef.current.focus();
        }
    };

    useEffect(() => {
        const button = buttonRef.current;
        const dropDown = dropDownRef.current;

        if (showDropDown && button !== null && dropDown !== null) {
            const {top, left} = button.getBoundingClientRect();
            const dropdownHeight = dropDownRef.current?.offsetHeight || 0;
            const windowHeight = window.innerHeight;
            const topPos = top + 40;

            if (topPos + dropdownHeight > windowHeight) {
                dropDown.style.top = `${top - dropdownHeight}px`;
            } else {
                dropDown.style.top = `${topPos}px`;
            }
            dropDown.style.left = `${Math.min(
                left,
                window.innerWidth - dropDown.offsetWidth - 20
            )}px`;
        }
    }, [showDropDown]);

    useEffect(() => {
        const button = buttonRef.current;

        if (button !== null && showDropDown) {
            const handle = (event: MouseEvent) => {
                const target = event.target;
                if (stopCloseOnClickSelf) {
                    if (dropDownRef.current && dropDownRef.current.contains(target as Node)) return;
                }
                if (!button.contains(target as Node)) {
                    setShowDropDown(false);
                }
            };
            document.addEventListener('click', handle);

            return () => document.removeEventListener('click', handle);
        }
        return noop;
    }, [dropDownRef, buttonRef, showDropDown, stopCloseOnClickSelf]);

    const renderButton = () => (
        <button
            {...createDataTestId(`toolbar-emoji-picker-icon`, false)}
            disabled={disabled}
            aria-label={buttonAriaLabel || buttonLabel}
            className={buttonClassName}
            onClick={() => setShowDropDown(!showDropDown)}
            ref={buttonRef}
        >
            {buttonIconClassName && <span className={buttonIconClassName} />}
            {buttonLabel && <span className="text dropdown-button-text">{buttonLabel}</span>}
            <IconCaret className="dropdown-button-chevron" />
        </button>
    );

    return (
        <>
            {withPopover ? (
                <IconWithPopover type="children" content={content}>
                    {renderButton()}
                </IconWithPopover>
            ) : (
                renderButton()
            )}

            {showDropDown &&
                createPortal(
                    <DropDownItems dropDownRef={dropDownRef} onClose={handleClose}>
                        {children}
                    </DropDownItems>,
                    (portalSelector && document.getElementById(portalSelector)) || document.body
                )}
        </>
    );
}
